<template>
  <div v-loading="loading">
    <el-table
      ref="table"
      row-key="id"
      v-if="rows && rows.length > 0"
      :data="rows"
      @sort-change="doChangeSort"
      @row-click="onRowClick"
    >
      <!-- School Name -->
      <el-table-column sortable="custom" :resizable="false" :label="fields.name.label" :prop="fields.name.name" min-width="160">
        <template slot-scope="scope">
          <el-tooltip
            :disabled="!hasPermissionToAuditLogs"
            effect="dark"
            :content="scope.row.id"
            placement="bottom"
          >
            <span
              class="list__avatar__name"
              v-text="presenter(scope.row, 'name')"
            />
          </el-tooltip>
        </template>
      </el-table-column>

      <!-- Teams -->
      <el-table-column :resizable="false" :label="fields.teams.label" :prop="fields.teams.name" min-width="100">
        <template slot-scope="scope">
          <app-i18n
            v-if="!scope.row.teams || !scope.row.teams.length"
            code="common.none"
          />
          <span
            v-else-if="scope.row.teams.length === 1"
            v-text="scope.row.teams[0].name"
          />
          <el-tooltip
            v-else-if="scope.row.teams.length > 1"
            effect="dark"
            :content="scope.row.teams.map(team => team.name).join('\n')"
            placement="bottom"
          >
            <app-i18n code="entities.team.list.multipleTeams" />
          </el-tooltip>
        </template>
      </el-table-column>

      <!-- Categories -->
      <el-table-column 
        :resizable="false" 
        :label="fields.subprograms.label" 
        :prop="fields.subprograms.name" 
        min-width="100"
      >
        <template slot-scope="scope">
          <app-i18n
            v-if="!scope.row.subprograms || !scope.row.subprograms.length"
            code="common.none"
          />
          <span
            v-else-if="scope.row.subprograms.length === 1"
            v-text="scope.row.subprograms[0].name"
          />
          <el-tooltip
            v-else-if="scope.row.subprograms.length > 1"
            effect="dark"
            :content="scope.row.subprograms.map(subprogram => subprogram.name).join('\n')"
            placement="bottom"
          >
            <app-i18n code="entities.school.list.multipleCategories" />
          </el-tooltip>
        </template>
      </el-table-column>

      <!-- Location -->
      <el-table-column :resizable="false" :label="fields.location.label" :prop="fields.location.name" min-width="140">
        <template slot-scope="scope">
          <span
            v-text="presenter(scope.row, 'location')"
          />
        </template>
      </el-table-column>

      <!-- Managers -->
      <el-table-column 
        :resizable="false" 
        :label="fields.coaches.label" 
        :prop="fields.coaches.name"
        min-width="160"
      >
        <template slot-scope="scope">
          <app-i18n
            v-if="!scope.row.coaches || !scope.row.coaches.length"
            code="common.none"
          />
          <span
            v-else-if="scope.row.coaches.length === 1"
            v-text="scope.row.coaches[0].email"
          />
          <el-tooltip
            v-else-if="scope.row.coaches.length > 1"
            effect="dark"
            :content="scope.row.coaches.map(coach => coach.email).join('\n')"
            placement="bottom"
          >
            <app-i18n code="entities.coach.list.multipleCoaches" />
          </el-tooltip>
        </template>
      </el-table-column>

      <!-- <el-table-column
        v-if="hasPermissionToAuditLogs"
        :label="fields.createdAt.label"
        :prop="fields.createdAt.name"
        sortable="custom"
      >
        <template slot-scope="scope">{{ presenter(scope.row, 'createdAt') }}</template>
      </el-table-column> -->

      <el-table-column :fixed="isMobile? undefined : 'right'" align="center" min-width="50">
        <template slot-scope="scope">
          <div class="table-actions">
            <router-link 
              :to="{
                name: 'schoolEdit',
                params: {
                  id: scope.row.id
                } 
              }" 
              v-if="hasPermissionToEdit"
            >
              <el-button
                icon="icon-pen"
                circle
              />
            </router-link>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <app-empty-page
      v-else
      :icon-class="'icon-teams'"
      :empty-text="i18n('entities.school.empty')"
    />

    <div class="el-pagination-wrapper" v-if="rows && rows.length > 0">
      <el-pagination
        :current-page="pagination.currentPage || 1"
        :disabled="loading"
        :layout="paginationLayout"
        :total="count"
        @current-change="doChangePaginationCurrentPage"
        @size-change="doChangePaginationPageSize"
      ></el-pagination>
    </div>
  </div>
</template>

<script>
import { AuditLogPermissions } from '@modules/audit-log/audit-log-permissions';
import { SchoolModel } from '@modules/school/school-model';
import SchoolListFilter from '@modules/school/components/school-list-filter.vue';
import { mapGetters, mapActions } from 'vuex';
import { SchoolPermissions } from '@modules/school/school-permissions';
import { i18n } from '@shared/i18n';
import { get } from 'lodash';

const { fields } = SchoolModel;
const IS_DISABLED_ACTIONS_COLUMN = true;

export default {
  name: 'app-school-list-table',

  components: {
    [SchoolListFilter.name]: SchoolListFilter,
  },

  data() {
    return {
      activeFilter: false,
    };
  },

  mounted() {
    if (!this.subpropertyId) return;
    this.doFetch();
  },

  computed: {
    ...mapGetters({
      rows: 'school/list/rows',
      count: 'school/list/count',
      loading: 'school/list/loading',
      pagination: 'school/list/pagination',
      isMobile: 'layout/isMobile',
      currentUser: 'auth/currentUser',
      destroyLoading: 'school/destroy/loading',
      paginationLayout: 'layout/paginationLayout',
      currentManage: 'auth/manage/currentManage',
    }),

    subpropertyId() {
      return get(
        this,
        'currentManage.subproperty.id',
        null,
      );
    },

    hasPermissionToAuditLogs() {
      return new AuditLogPermissions(this.currentUser).read;
    },

    hasPermissionToEdit() {
      return new SchoolPermissions(this.currentUser).edit;
    },

    hasPermissionToDestroy() {
      return new SchoolPermissions(this.currentUser).destroy;
    },

    fields() {
      return fields;
    },

    isDisabledActionsColumn() {
      return IS_DISABLED_ACTIONS_COLUMN;
    }
  },

  methods: {
    ...mapActions({
      doChangeSort: 'school/list/doChangeSort',
      doChangePaginationCurrentPage:
        'school/list/doChangePaginationCurrentPage',
      doChangePaginationPageSize:
        'school/list/doChangePaginationPageSize',
      doDestroy: 'school/destroy/doDestroy',
      doFetch: 'school/list/doFetch',
    }),

    onRowClick(row, column) {
      if (!('label' in column)) return;
      this.$router.push({
        name: 'schoolView',
        params: {
          id: row.id,
        }
      });
    },
    presenter(row, fieldName) {
      return SchoolModel.presenter(row, fieldName);
    },

    async doDestroyWithConfirm(id) {
      try {
        await this.$confirm(
          i18n('common.areYouSure'),
          i18n('common.confirm'),
          {
            confirmButtonText: i18n('common.yes'),
            cancelButtonText: i18n('common.no'),
            type: 'warning',
          },
        );

        return this.doDestroy({id, subpropertyId: this.subpropertyId});
      } catch (error) {
        // No
      }
    },

    i18n(code) {
      return i18n(code);
    },
  },
};
</script>

<style>
</style>
